// The workspace's side of the phone's Dock (Me's extension `dock`): the
// sections of the workspace being looked at, or, from Me, of the last one
// looked at on this device (else the first one joined). Switching workspaces
// changes only this side.
//
// Promise: the items are the sections the Workspace sidebar shows, in its
// order and with its dots; the menu switches to another workspace, opens this
// one's settings and makes a new one.
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { workspacePlace } from "@trameria/cloud-api/workspace-shared";
import { m } from "@trameria/cloud-i18n";
import {
  type DockExtension,
  type DockPlace,
  defineDeviceSetting,
  favoritesQuery,
  iconNameOf,
  type MenuItem,
  type ShellFavoriteEntry,
  type ShellFavorites,
  useApi,
  useDeviceSetting,
  useMeApp,
} from "@trameria/ui-core";
import { type ReactNode, useEffect, useMemo } from "react";
import {
  CurrentWorkspaceContext,
  type WorkspaceHost,
  WorkspaceHostContext,
} from "./context";
import { MY_THINGS_PATHS } from "./me-place";
import { placePage } from "./place-pages";
import { spacesQuery, workspacesQuery, workspaceViewQuery } from "./queries";
import { sectionShown } from "./sections";
import { WorkspaceTile } from "./tile";

/** The workspace last looked at on this device (its slug). */
const lastWorkspace = defineDeviceSetting<string | null>({
  key: "trameria-phone-dock-workspace",
  parse: (raw) => raw || null,
  serialize: (v) => v ?? "",
});

/** The workspace last looked at on this device (its slug), read outside React. */
export const lastWorkspaceSlug = (): string | null => lastWorkspace.read();

/**
 * The workspace's context around its sections' badge hooks (they are drawn in
 * Me too). One per workspace, so switching keeps nothing of another.
 */
function scopeOf(host: WorkspaceHost, slug: string) {
  return function WorkspaceScope({ children }: { children: ReactNode }) {
    const view = useQuery(workspaceViewQuery(useApi(), slug)).data;
    if (!view) return null;
    return (
      <WorkspaceHostContext.Provider value={host}>
        <CurrentWorkspaceContext.Provider value={{ slug, view }}>
          {children}
        </CurrentWorkspaceContext.Provider>
      </WorkspaceHostContext.Provider>
    );
  };
}

export function workspaceDock(host: WorkspaceHost): DockExtension {
  const sections = host.sections;
  return {
    usePlace(): DockPlace | null {
      const api = useApi();
      const navigate = useNavigate();
      const params = useParams({ strict: false }) as { slug?: string };
      const list = useQuery(workspacesQuery(api)).data;
      const [last, setLast] = useDeviceSetting(lastWorkspace);
      const shownSlug =
        params.slug && list?.some((w) => w.slug === params.slug)
          ? params.slug
          : null;
      // frontend-development: not a fetch; remembers the workspace looked at on this device.
      useEffect(() => {
        if (shownSlug && shownSlug !== last) setLast(shownSlug);
      }, [shownSlug, last, setLast]);
      const slug =
        shownSlug ??
        (last && list?.some((w) => w.slug === last) ? last : null) ??
        list?.[0]?.slug ??
        null;
      const view = useQuery({
        ...workspaceViewQuery(api, slug ?? ""),
        enabled: slug !== null,
      }).data;
      const spaces = useQuery({
        ...spacesQuery(api, slug ?? ""),
        enabled: slug !== null,
      }).data;
      const Scope = useMemo(
        () => (slug ? scopeOf(host, slug) : null),
        [host, slug],
      );
      // The favorites kept in this workspace, as data (a host draws them).
      const favorites = useFavoritesData(view ? workspacePlace(view.id) : null);
      return useMemo(() => {
        if (!slug || !view || !Scope) return null;
        const live = (spaces ?? []).filter((s) => s.archivedAt === null);
        const items = sections
          .filter((s) => sectionShown(s, view, spaces ?? []))
          .map((s) => ({
            id: s.id,
            label: s.label,
            icon: s.icon,
            to: `/${slug}${s.to}`,
            // A section's lists behind tabs: its entry is current on each.
            paths: s.paths?.map((p) => `/${slug}${p}`),
            useBadge: s.useBadge,
          }));
        const others: MenuItem[] = (list ?? [])
          .filter((w) => w.slug !== slug)
          .map((w) => ({
            id: `ws:${w.id}`,
            label: w.name,
            onSelect: () =>
              void navigate({ to: placePage(w.slug, `/${w.slug}`) }),
          }));
        return {
          id: workspacePlace(view.id),
          // Its own sidebar as data, for a host that draws it (the desktop
          // app): what the Workspace sidebar shows, in its order.
          sidebar: {
            mine: [
              ...sections
                .filter((s) => s.mine && sectionShown(s, view, spaces ?? []))
                .map((s) => `place:${s.id}`),
              ...MY_THINGS_PATHS.map((p) => `me:${p}`),
            ],
            workspace: view.id,
            menu: [
              {
                id: "settings",
                label: m.ws_menu_settings(),
                icon: "settings",
                onSelect: () => void navigate({ to: `/${slug}/settings` }),
              },
              ...(view.me.isOwner ||
              view.me.permissions.includes("workspace.invite")
                ? [
                    {
                      id: "invite",
                      label: m.ws_menu_invite(),
                      icon: "invite" as const,
                      onSelect: () =>
                        void navigate({ to: `/${slug}/settings/invites/new` }),
                    },
                  ]
                : []),
              {
                id: "new",
                label: m.ws_menu_new(),
                icon: "plus",
                onSelect: () => void navigate({ to: "/new" }),
              },
            ],
            ...(favorites ? { favorites } : {}),
          },
          name: view.name,
          mark: (
            <WorkspaceTile
              id={view.id}
              name={view.name}
              iconFileId={view.iconFileId}
              size={20}
              current
            />
          ),
          items,
          Scope,
          // Its spaces' overviews, when there is more than one (one space is
          // the workspace itself: no heading, as in the lists).
          lists: [
            {
              heading: m.ws_dock_spaces(),
              links:
                live.length > 1
                  ? live.map((s) => ({
                      id: s.id,
                      label: s.name,
                      to: `/${slug}/spaces/${s.slug}`,
                    }))
                  : [],
            },
          ],
          menu: [
            ...others,
            ...(others.length > 0 ? ["separator" as const] : []),
            {
              id: "settings",
              label: m.ws_menu_settings(),
              onSelect: () => void navigate({ to: `/${slug}/settings` }),
            },
            {
              id: "new",
              label: m.ws_menu_new(),
              onSelect: () => void navigate({ to: "/new" }),
            },
          ],
        };
      }, [sections, slug, view, spaces, list, navigate, Scope, favorites]);
    },
  };
}

/**
 * A place's favorites as data (`ShellFavorites`): the kinds this app draws,
 * with their icon's name and where each opens. Null until read.
 */
function useFavoritesData(place: string | null): ShellFavorites | null {
  const { favoriteKinds } = useMeApp();
  const data = useQuery({
    ...favoritesQuery(useApi(), place ?? ""),
    enabled: place !== null,
  }).data;
  return useMemo(() => {
    if (!place || !data) return null;
    const entries: ShellFavoriteEntry[] = [];
    const item = (i: {
      id: string;
      kind: string;
      targetId: string;
      title: string;
    }) => {
      const kind = favoriteKinds[i.kind];
      if (!kind) return null;
      const icon = iconNameOf(kind.icon);
      return {
        type: "item" as const,
        id: i.id,
        label: i.title,
        ...(icon ? { icon } : {}),
        to: kind.to(i.targetId),
      };
    };
    for (const e of data.root) {
      if (e.type === "folder")
        entries.push({
          type: "folder",
          id: e.id,
          label: e.name,
          items: e.items.flatMap((x) => item(x) ?? []),
        });
      else {
        const one = item(e);
        if (one) entries.push(one);
      }
    }
    return { place, entries };
  }, [place, data, favoriteKinds]);
}
